import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Card,
  Stack,
  Button,
  Popover,
  MenuItem,
  Container,
  Typography,
  CircularProgress,
  Box,
  Tab,
  Tabs,
} from "@mui/material";
import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";
import { useNavigate } from "react-router-dom";
import { del, get, post } from "src/utils/networkUtils";
import moment from "moment";
import ActivePrizeDrawTable from "src/components/prize-draw-admin/ActivePrizeDrawTable";
import DeletePrizeDrawModal from "src/components/prize-draw-admin/DeletePrizeDrawModal";
import PendingDrawTable from "src/components/prize-draw-admin/PendingDrawTable";
import DefaultDetailDialog from "src/components/prize-draw-admin/DefaultDetail";
import { getUserPermissions } from "src/utils/permissionsUtils";
import useWithUrlTenant from "src/hooks/useTenant";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1, paddingTop: 3 }}>{children}</Box>}
    </div>
  );
}
const dateFormat = "YYYY-MM-DD HH:mm:ss";
export default function PrizeDrawsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [rows, setRows] = useState(null);
  const [openPrizeDrawDialog, setOpenPrizeDrawDialog] = useState(false);
  const [prizeDraw, setPrizeDraw] = useState(null);
  const [value, setValue] = useState(0);
  const [prizeDrawList, setPrizeDrawList] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userIds, setUserIds] = useState("");
  const [prizeDrawEntryData, setPrizeDrawEntryData] = useState([]);
  const withTenant = useWithUrlTenant();

  useEffect(() => {
    get(withTenant(`/admin/prize-draws?limit=1000&offset=0`)).then(function (
      response
    ) {
      const updatedRows = response?.prizeDraws?.filter(Boolean) ?? [];
      const updated = updatedRows.filter((row) => {
        if (!row) return false;
        return row.status === "active";
      });
      setPrizeDrawList(updated);
      setRows(updatedRows);
    });
  }, [withTenant]);
  const handleOpenMenu = (event, data) => {
    setPrizeDraw(data);
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
  };
  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };
  const handleOpenEditStageDialog = (prizeDrawId) => {
    setOpen(null);
    setOpenId(null);
    navigate(`/dashboard/prize-draws/add`, {
      state: { prizeDraw },
    });
  };
  const handleDeletePrizeDraw = async (prizeDrawId) => {
    setLoading(true);
    setOpen(null);
    setOpenId(null);
    const resp = await del(`/admin/prize-draws/${prizeDrawId}`);
    if (resp.status === 204) {
      const userIds = resp.data?.map((item) => item.userId).join(",");
      const refund = resp.data?.map((item) => {
        return {
          userId: item.userId,
          amount: item.amount,
        };
      });
      setUserIds(userIds);
      setPrizeDrawEntryData(refund);
      setLoading(false);
      setDeleteModalOpen(true);
    }
    if (resp.success) {
      setLoading(false);
      window.location.reload(true);
    }
  };
  const handleConfirmDelete = async (options) => {
    if (options.refund) {
      await post(`/admin/tickets/refund`, prizeDrawEntryData);
    }
    if (options.notify) {
      const body = {
        subject: "Prize Draw Deleted",
        body: `Prize Draw has been deleted`,
        specificUserIds: userIds,
        status: 1,
        auditory: "userids",
        publishAt: moment().format(dateFormat),
      };
      await post(`/admin/inbox-messages`, body);
    }
    const resp = await del(`/admin/prize-draws/entries/${userIds}`);
    if (resp.success) {
      handleDeletePrizeDraw(prizeDraw.prizeDrawId);
    }
  };
  const handleChangeTabs = (event, newValue) => {
    setValue(newValue);
    if (+newValue === 0) {
      const updated = rows?.filter((row) => {
        if (!row) return false;
        return row.status === "active";
      });
      setPrizeDrawList(updated);
    }
    if (+newValue === 1) {
      const updated = rows.filter((row) => {
        if (!row) return false;
        return row.status === "pending";
      });
      setPrizeDrawList(updated);
    } else if (+newValue === 2) {
      const updated = rows.filter((row) => {
        return row.status === "archived";
      });
      setPrizeDrawList(updated);
    } else if (+newValue === 3) {
      const updated = rows.filter((row) => {
        if (!row) return false;
        return row.status === "draft";
      });
      setPrizeDrawList(updated);
    }
  };
  const canEditOrDeletePrizeDraw = (prizeDraw) => {
    const { isViewer } = getUserPermissions();

    if (isViewer) return false;
    if (!prizeDraw || !prizeDraw.drawDate || !prizeDraw.status) {
      return false;
    }

    return prizeDraw.status !== "archived";
  };
  const handleExportCsv = (prizeDrawId) => {
    get(
      withTenant(`/admin/prize-draws/csv/${prizeDrawId}`, {
        responseType: "blob",
      })
    ).then(function (response) {
      const downloadUrl = window.URL.createObjectURL(new Blob([response]));

      const link = document.createElement("a");

      link.href = downloadUrl;

      link.setAttribute("download", `${prizeDraw?.title}-${prizeDrawId}.csv`);

      document.body.appendChild(link);

      link.click();

      link.remove();
    });
  };

  const handleSeeWinners = (prizeDrawId) => {
    navigate(`/dashboard/prize-draws/${prizeDrawId}/winner-list`, {
      state: { prizes: prizeDraw },
    });
  };

  return (
    <>
      <Helmet>
        <title> Prize Draws </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Prize Draws
          </Typography>
          <Stack spacing={2} direction="row">
            <Button
              variant="outlined"
              onClick={() => setOpenPrizeDrawDialog(true)}
              startIcon={
                <img
                  src={"/assets/icons/clipboard.svg"}
                  alt={"/assets/icons/clipboard.svg"}
                  style={{ width: 20, height: 20 }}
                />
              }
            >
              Default Details
            </Button>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-outline" />}
              onClick={() => navigate("/dashboard/prize-draws/add")}
            >
              Create Prize Draw
            </Button>
          </Stack>
        </Stack>

        <Card>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
            >
              <Tab label="Active Prize Draws" {...a11yProps(0)} />
              <Tab label="Pending Draw Date" {...a11yProps(1)} />
              <Tab label="Archived" {...a11yProps(2)} />
              <Tab label="Draft" {...a11yProps(3)} />
            </Tabs>
          </Box>
          <TabPanel value={value} index={0}>
            {rows === null || loading ? (
              <Box
                sx={{ display: "flex", justifyContent: "center" }}
                style={{ padding: 20 }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Scrollbar>
                <ActivePrizeDrawTable
                  rows={prizeDrawList}
                  handleOpenMenu={handleOpenMenu}
                />
              </Scrollbar>
            )}
          </TabPanel>
          <TabPanel value={value} index={1}>
            <Scrollbar>
              <PendingDrawTable
                rows={prizeDrawList}
                handleOpenMenu={handleOpenMenu}
              />
            </Scrollbar>
          </TabPanel>
          <TabPanel value={value} index={2}>
            <Scrollbar>
              <ActivePrizeDrawTable
                rows={prizeDrawList}
                handleOpenMenu={handleOpenMenu}
              />
            </Scrollbar>
          </TabPanel>
          <TabPanel value={value} index={3}>
            <Scrollbar>
              <ActivePrizeDrawTable
                rows={prizeDrawList}
                handleOpenMenu={handleOpenMenu}
              />
            </Scrollbar>
          </TabPanel>
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            "& .MuiMenuItem-root": {
              px: 1,
              typography: "body2",
              borderRadius: 0.75,
            },
          },
        }}
      >
        {canEditOrDeletePrizeDraw(prizeDraw) && (
          <MenuItem onClick={() => handleOpenEditStageDialog(openId)}>
            Edit
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleSeeWinners(openId);
          }}
        >
          See Winners
        </MenuItem>
        <MenuItem onClick={() => handleExportCsv(openId)}>Export List</MenuItem>
        {canEditOrDeletePrizeDraw(prizeDraw) && (
          <MenuItem onClick={() => handleDeletePrizeDraw(openId)}>
            Delete
          </MenuItem>
        )}
      </Popover>

      {openPrizeDrawDialog && (
        <DefaultDetailDialog
          open={openPrizeDrawDialog}
          handleClose={() => setOpenPrizeDrawDialog(false)}
        />
      )}

      {deleteModalOpen && (
        <DeletePrizeDrawModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          prizeDraw={prizeDraw}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
}
